import useTranslation from 'next-translate/useTranslation';
import type { ContestSettings, SlateLabelEnum } from '@betterpool/api-types/contests-service';
import { ResizingMode } from '@betterpool/api-types/contests-service';
import { Flex } from '@mantine/core';

import { Match } from '~/domains/common/components/Match/Match';
import { Text } from '~/domains/design-system/Text';
import { BadgeGuaranteed, BadgeResizable } from '~/components/shared/Badges/Badges';
import GuaranteedPlusBadge from '~/domains/design-system/badges/GuaranteedPlusBadge/GuaranteedPlusBadge';
import dt from '~/testing';

import { SectionNote } from '../components';

import PrizesSegmentsFooter from './PrizesSegmentsFooter';
import {
  type Contest,
  AdditionalPayoutType,
} from '~/domains/contest/domains/services/interfaces/contest.interface';
import { useAdditionalPayoutSettings } from '~/domains/contest/domains/common/hooks/useAdditionalPayoutSettings';

const RESIZING_MODE_TEST_ID = {
  [ResizingMode.RESIZABLE]: dt.contestDetail.section.prizes.resizableContest,
  [ResizingMode.GUARANTEED]: dt.contestDetail.section.prizes.guaranteedContest,
  [ResizingMode.STANDARD]: dt.contestDetail.section.prizes.standardContest,
};

type PrizesResizingInfoProps = {
  resizingMode?: ResizingMode;
  segments: ContestSettings['segments'];
  slateLabel: SlateLabelEnum;
  contest: Contest;
};

function PrizesResizingInfo({
  resizingMode,
  segments,
  slateLabel,
  contest,
}: PrizesResizingInfoProps) {
  const { t } = useTranslation('contest');
  const { type, isEnabled: isGuaranteedPlusEnabled } = useAdditionalPayoutSettings(contest);

  // Check if guaranteed plus is enabled and takes precedence over the resizing mode
  const isGuaranteedPlus = isGuaranteedPlusEnabled && type === AdditionalPayoutType.GUARANTEED_PLUS;

  return (
    <SectionNote data-test-id={RESIZING_MODE_TEST_ID[resizingMode]}>
      <Text variant="body-medium">
        {isGuaranteedPlus ? (
          t('detail.prizes.guaranteedPlusContest')
        ) : (
          <Match value={resizingMode}>
            <Match.On value={[ResizingMode.RESIZABLE]}>
              <PrizesSegmentsFooter segments={segments} slateLabel={slateLabel} />
            </Match.On>
            <Match.On value={[ResizingMode.STANDARD]}>
              {t('detail.prizes.standardContest')}
            </Match.On>
            <Match.On value={[ResizingMode.GUARANTEED]}>
              {t('detail.prizes.guaranteedContest')}
            </Match.On>
          </Match>
        )}
      </Text>
    </SectionNote>
  );
}

type PrizesResizingModeProps = {
  resizingMode?: ResizingMode;
  contest: Contest;
};

function PrizesResizingMode({ resizingMode, contest }: PrizesResizingModeProps) {
  const { t } = useTranslation('contest');
  const { type, isEnabled: isGuaranteedPlusEnabled } = useAdditionalPayoutSettings(contest);

  // Check if guaranteed plus is enabled and takes precedence over the resizing mode ^^
  const isGuaranteedPlus = isGuaranteedPlusEnabled && type === AdditionalPayoutType.GUARANTEED_PLUS;

  return (
    <SectionNote mb={4}>
      <Text variant="subhead-small" c="gray.6">
        {isGuaranteedPlus ? (
          <Flex align="center" /* eslint-disable-line local-rules/use-theme-tokens */ gap={8}>
            {t('detail.prizes.resizingMode.guaranteedPlus')} <GuaranteedPlusBadge size="micro" />
          </Flex>
        ) : (
          <Match value={resizingMode}>
            <Match.On value={[ResizingMode.RESIZABLE]}>
              <Flex align="center" /* eslint-disable-line local-rules/use-theme-tokens */ gap={8}>
                {t('detail.prizes.resizingMode.resizable')} <BadgeResizable iconSize={14} />
              </Flex>
            </Match.On>
            <Match.On value={[ResizingMode.GUARANTEED]}>
              <Flex align="center" /* eslint-disable-line local-rules/use-theme-tokens */ gap={8}>
                {t('detail.prizes.resizingMode.guaranteed')} <BadgeGuaranteed iconSize={14} />
              </Flex>
            </Match.On>
            <Match.On value={[ResizingMode.STANDARD]}>
              {t('detail.prizes.resizingMode.standard')}
            </Match.On>
          </Match>
        )}
      </Text>
    </SectionNote>
  );
}

export { PrizesResizingMode, PrizesResizingInfo };
